
import { KeyValuationMetrics, PeerComparison } from './types';

/**
 * Calculate Enterprise Value (EV) 
 * @param marketCap - Market capitalization
 * @param debt - Total debt
 * @param cash - Cash and cash equivalents
 * @returns Enterprise Value
 */
export function calculateEnterpriseValue(
  marketCap: number,
  debt: number,
  cash: number
): number {
  return marketCap + debt - cash;
}

/**
 * Calculate Enterprise Value to Resource ratio
 * @param enterpriseValue - Enterprise Value
 * @param resourceSize - Resource size in tonnes or ounces
 * @returns EV/Resource ratio
 */
export function calculateEVToResourceRatio(
  enterpriseValue: number,
  resourceSize: number
): number {
  if (resourceSize === 0) return 0;
  return enterpriseValue / resourceSize;
}

/**
 * Calculate Price to Net Asset Value ratio
 * @param marketCap - Market capitalization
 * @param nav - Net Asset Value
 * @returns P/NAV ratio
 */
export function calculatePriceToNAVRatio(
  marketCap: number,
  nav: number
): number {
  if (nav === 0) return 0;
  return marketCap / nav;
}

/**
 * Calculate Enterprise Value to EBITDA ratio
 * @param enterpriseValue - Enterprise Value
 * @param ebitda - Earnings Before Interest, Taxes, Depreciation, and Amortization
 * @returns EV/EBITDA ratio
 */
export function calculateEVToEBITDARatio(
  enterpriseValue: number,
  ebitda: number
): number {
  if (ebitda === 0) return 0;
  return enterpriseValue / ebitda;
}

/**
 * Resource data interface for valuation calculations
 */
interface ResourceData {
  tonnage: number;
  grade: number;
  mineralType: string;
  classification: 'indicated' | 'inferred' | 'measured';
  marketValue: number;
}

/**
 * Market data interface for valuation calculations
 */
interface MarketData {
  goldPrice: number;
  silverPrice: number;
  copperPrice: number;
  commodityTrends: {
    gold: string;
    silver: string;
    copper: string;
  };
}

/**
 * Cost structure interface for valuation calculations
 */
interface CostStructure {
  miningCosts: number;
  processingCosts: number;
  administrativeCosts: number;
  transportationCosts: number;
}

/**
 * Risk factors interface for valuation calculations
 */
interface RiskFactors {
  geological: number;
  political: number;
  environmental: number;
  technical: number;
  market: number;
}

/**
 * Financial parameters interface for valuation calculations
 */
interface FinancialParameters {
  discountRate: number;
  taxRate: number;
  royaltyRate: number;
}

/**
 * Calculate key valuation metrics for a mining project with comprehensive parameters
 * @param resourceData - Resource data
 * @param marketData - Market data including commodity prices
 * @param costStructure - Cost structure data
 * @param riskFactors - Risk factors assessment
 * @param financialParameters - Financial parameters
 * @returns Comprehensive valuation metrics
 */
export function calculateKeyValuationMetrics(
  resourceData: ResourceData,
  marketData: MarketData,
  costStructure: CostStructure,
  riskFactors: RiskFactors,
  financialParameters: FinancialParameters
): any {
  // Calculate total costs per tonne
  const totalCosts = costStructure.miningCosts + costStructure.processingCosts + 
                    costStructure.administrativeCosts + costStructure.transportationCosts;

  // Get commodity price based on mineral type
  let commodityPrice = 0;
  if (resourceData.mineralType.toLowerCase().includes('gold')) {
    commodityPrice = marketData.goldPrice;
  } else if (resourceData.mineralType.toLowerCase().includes('silver')) {
    commodityPrice = marketData.silverPrice;
  } else if (resourceData.mineralType.toLowerCase().includes('copper')) {
    commodityPrice = marketData.copperPrice;
  }

  // Calculate revenue per tonne (grade * commodity price)
  const revenuePerTonne = resourceData.grade * commodityPrice;
  
  // Calculate gross profit per tonne
  const grossProfitPerTonne = revenuePerTonne - totalCosts;
  
  // Calculate total gross value
  const totalGrossValue = grossProfitPerTonne * resourceData.tonnage;
  
  // Apply taxes and royalties
  const afterTaxValue = totalGrossValue * (1 - financialParameters.taxRate) * (1 - financialParameters.royaltyRate);
  
  // Calculate NPV using discount rate
  const netPresentValue = afterTaxValue / (1 + financialParameters.discountRate);
  
  // Calculate IRR (simplified calculation)
  const internalRateOfReturn = grossProfitPerTonne > 0 ? 
    (grossProfitPerTonne / totalCosts) * 0.1 : 0;
  
  // Calculate payback period (simplified)
  const paybackPeriod = totalCosts > 0 ? resourceData.marketValue / (grossProfitPerTonne * resourceData.tonnage * 0.1) : 0;
  
  // Calculate profitability index
  const profitabilityIndex = resourceData.marketValue > 0 ? netPresentValue / resourceData.marketValue : 0;
  
  // Calculate overall risk score (weighted average)
  const riskScore = (
    riskFactors.geological * 20 +
    riskFactors.political * 15 +
    riskFactors.environmental * 15 +
    riskFactors.technical * 25 +
    riskFactors.market * 25
  ) * 100;
  
  // Individual risk components
  const marketRisk = riskFactors.market;
  const technicalRisk = riskFactors.technical;
  const politicalRisk = riskFactors.political;
  
  // Generate sensitivity analysis
  const sensitivityAnalysis = [
    {
      variable: 'commodity price',
      impact: 0.15 // 15% impact on NPV for 10% price change
    },
    {
      variable: 'operating costs',
      impact: -0.12 // -12% impact on NPV for 10% cost increase
    },
    {
      variable: 'resource grade',
      impact: 0.18 // 18% impact on NPV for 10% grade change
    },
    {
      variable: 'discount rate',
      impact: -0.08 // -8% impact on NPV for 10% discount rate change
    }
  ];

  return {
    netPresentValue,
    internalRateOfReturn,
    paybackPeriod,
    profitabilityIndex,
    riskScore,
    marketRisk,
    technicalRisk,
    politicalRisk,
    sensitivityAnalysis,
    totalGrossValue,
    afterTaxValue,
    revenuePerTonne,
    grossProfitPerTonne
  };
}

/**
 * Calculate key valuation metrics for a mining project (legacy version)
 * @param tenement - Tenement object
 * @param marketCap - Market capitalization
 * @param debt - Total debt
 * @param cash - Cash and cash equivalents
 * @param nav - Net Asset Value
 * @param ebitda - EBITDA (for production stage only)
 * @param peerComparisonData - Peer comparison data
 * @returns Key valuation metrics
 */
export function calculateLegacyValuationMetrics(
  tenement: any,
  marketCap: number,
  debt: number,
  cash: number,
  nav: number,
  ebitda: number = 0,
  peerComparisonData: PeerComparison[] = []
): KeyValuationMetrics {
  // Calculate Enterprise Value
  const enterpriseValue = calculateEnterpriseValue(marketCap, debt, cash);
  
  // Get resource size
  let resourceSize = 0;
  if (tenement.resourceEstimation) {
    resourceSize = tenement.resourceEstimation.tonnage;
  }
  
  // Calculate ratios
  const evToResourceRatio = calculateEVToResourceRatio(enterpriseValue, resourceSize);
  const priceToNavRatio = calculatePriceToNAVRatio(marketCap, nav);
  
  // Only calculate EV/EBITDA for production stage
  let evToEbitdaRatio;
  if (tenement.status === "production" && ebitda > 0) {
    evToEbitdaRatio = calculateEVToEBITDARatio(enterpriseValue, ebitda);
  }
  
  return {
    enterpriseValue,
    evToResourceRatio,
    priceToNavRatio,
    evToEbitdaRatio,
    peerComparisonData
  };
}

/**
 * Generate sample peer comparison data for demonstration
 * @param mineralType - Type of mineral for filtering peers
 * @param projectStage - Project stage for filtering peers
 * @returns Array of peer comparison data
 */
export function generateSamplePeerComparisonData(
  mineralType: string,
  projectStage: "prospecting" | "exploration" | "development" | "production"
): PeerComparison[] {
  // Sample peers for gold projects
  const goldPeers: PeerComparison[] = [
    {
      peerName: "GoldCorp Exploration",
      resourceSize: 3500000,
      resourceGrade: 2.1,
      marketCap: 450000000,
      enterpriseValue: 430000000,
      evToResourceRatio: 123,
      priceToNavRatio: 0.85,
      stage: "exploration"
    },
    {
      peerName: "PreciousMetals Inc",
      resourceSize: 2800000,
      resourceGrade: 1.8,
      marketCap: 380000000,
      enterpriseValue: 410000000,
      evToResourceRatio: 146,
      priceToNavRatio: 0.92,
      stage: "exploration"
    },
    {
      peerName: "Golden Prospects",
      resourceSize: 1200000,
      resourceGrade: 3.2,
      marketCap: 220000000,
      enterpriseValue: 215000000,
      evToResourceRatio: 179,
      priceToNavRatio: 0.78,
      stage: "exploration"
    },
    {
      peerName: "NuggetMines",
      resourceSize: 5600000,
      resourceGrade: 1.5,
      marketCap: 950000000,
      enterpriseValue: 1100000000,
      evToResourceRatio: 196,
      priceToNavRatio: 1.1,
      evToEbitdaRatio: 8.5,
      stage: "production"
    },
    {
      peerName: "Goldfields Development",
      resourceSize: 4100000,
      resourceGrade: 2.3,
      marketCap: 720000000,
      enterpriseValue: 850000000,
      evToResourceRatio: 207,
      priceToNavRatio: 0.95,
      stage: "development"
    }
  ];
  
  // Sample peers for copper projects
  const copperPeers: PeerComparison[] = [
    {
      peerName: "Copper Mountain",
      resourceSize: 350000000,
      resourceGrade: 0.45,
      marketCap: 680000000,
      enterpriseValue: 750000000,
      evToResourceRatio: 2.14,
      priceToNavRatio: 0.88,
      stage: "exploration"
    },
    {
      peerName: "RedMetal Exploration",
      resourceSize: 280000000,
      resourceGrade: 0.6,
      marketCap: 520000000,
      enterpriseValue: 540000000,
      evToResourceRatio: 1.93,
      priceToNavRatio: 0.82,
      stage: "exploration"
    },
    {
      peerName: "CopperOne",
      resourceSize: 620000000,
      resourceGrade: 0.35,
      marketCap: 1200000000,
      enterpriseValue: 1350000000,
      evToResourceRatio: 2.18,
      priceToNavRatio: 0.97,
      evToEbitdaRatio: 7.2,
      stage: "production"
    }
  ];
  
  // Sample peers for early stage projects
  const earlyPeers: PeerComparison[] = [
    {
      peerName: "Early Minerals",
      resourceSize: 0,
      resourceGrade: 0,
      marketCap: 45000000,
      enterpriseValue: 40000000,
      evToResourceRatio: 0,
      priceToNavRatio: 0.75,
      stage: "prospecting"
    },
    {
      peerName: "Prospect Ventures",
      resourceSize: 0,
      resourceGrade: 0,
      marketCap: 35000000,
      enterpriseValue: 30000000,
      evToResourceRatio: 0,
      priceToNavRatio: 0.7,
      stage: "prospecting"
    }
  ];
  
  // Filter peers by mineral type and stage
  let relevantPeers: PeerComparison[] = [];
  if (mineralType.toLowerCase().includes('gold')) {
    relevantPeers = goldPeers;
  } else if (mineralType.toLowerCase().includes('copper')) {
    relevantPeers = copperPeers;
  } else if (projectStage === "prospecting") {
    relevantPeers = earlyPeers;
  } else {
    // Mix of peers for other minerals
    relevantPeers = [...goldPeers.slice(0, 2), ...copperPeers.slice(0, 2)];
  }
  
  // Filter by stage if not prospecting (prospecting usually has no resources yet)
  if (projectStage !== "prospecting") {
    relevantPeers = relevantPeers.filter(peer => 
      peer.stage === projectStage || 
      // Include adjacent stages for better comparison
      (projectStage === "exploration" && peer.stage === "development") ||
      (projectStage === "development" && (peer.stage === "exploration" || peer.stage === "production"))
    );
  }
  
  // Return at least some peers even if filtering leaves none
  return relevantPeers.length > 0 ? relevantPeers : 
         (projectStage === "prospecting" ? earlyPeers : goldPeers.slice(0, 3));
}
